/**
 * Dashboard parameter bar (S10) — one chip per declared parameter. A value
 * change persists to the uiState sidecar, fires analytics ONLY on an actual
 * change, and clears downstream `dependsOn` selections in cascade order
 * (their options re-fetch against the new upstream value; a stale selection
 * must never silently keep filtering).
 */
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { BoundParams, Dashboard, DashboardParameter } from '../../types/dashboardTypes';
import { cascadeInvalidationOrder } from '../../services/parameterBinding';
import { selectParamValues, useDashboardParamStore } from '../../store/dashboardParamStore';
import { FreeTextChip } from './FreeTextChip';
import { ListParamChip } from './ListParamChip';
import { QueryListParamChip } from './QueryListParamChip';
import { TimeRangeChip } from './TimeRangeChip';

export function ParameterChipsBar({ dashboard }: { dashboard: Dashboard }) {
  const values = useDashboardParamStore(selectParamValues(dashboard.id));
  const setValue = useDashboardParamStore((s) => s.setValue);
  const { track } = useAnalytics();

  if (dashboard.parameters.length === 0) return null;

  const commit = (def: DashboardParameter, id: string, next: BoundParams[string]) => {
    const previous = values[id];
    if (JSON.stringify(previous ?? null) === JSON.stringify(next ?? null)) return;
    setValue(dashboard.id, id, next);
    for (const downstream of cascadeInvalidationOrder(dashboard.parameters, id)) {
      setValue(dashboard.id, downstream, undefined);
    }
    track('feature:dashboard_param_change', { kind: def.kind });
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 border-b border-border bg-background px-4 py-1.5"
      data-testid="dashboard-param-bar"
    >
      {dashboard.parameters.map((def) => {
        switch (def.kind) {
          case 'time-range':
            return (
              <TimeRangeChip
                key={def.id}
                defaultDays={def.defaultDays}
                value={values[def.id]}
                onChange={(next) => commit(def, def.id, next)}
              />
            );
          case 'static-list':
            return (
              <ListParamChip
                key={def.id}
                id={def.id}
                label={def.label}
                options={def.values.map((v) => ({ label: v, value: v }))}
                multi={def.multi === true}
                includeAll={def.includeAll === true}
                defaultValue={def.defaultValue}
                value={values[def.id]}
                onChange={(next) => commit(def, def.id, next)}
              />
            );
          case 'query-list':
            return (
              <QueryListParamChip
                key={def.id}
                dashboard={dashboard}
                def={def}
                values={values}
                value={values[def.id]}
                onChange={(next) => commit(def, def.id, next)}
              />
            );
          case 'free-text':
            return (
              <FreeTextChip
                key={def.id}
                id={def.id}
                label={def.label}
                defaultValue={def.defaultValue}
                value={values[def.id]}
                onChange={(next) => commit(def, def.id, next)}
              />
            );
        }
      })}
    </div>
  );
}
